import { useCallback, useEffect, useRef, useState } from 'react';
import { useListStringsLazyQuery } from 'src/generated/graphql';
import { StringInfo } from '../types';
import { fetchVersionSnapshot } from '../utils/fetchVersionSnapshot';

interface UseStringsDataResult {
  strings: StringInfo[];
  loading: boolean;
  /** As thrown, not coerced: the renderer keys on the GraphQL `errorType` and on whether the text
   * is the service's wording or a guard code, neither of which survives a string. */
  error: { thrown: unknown } | null;
  /** Re-fetches the snapshot (e.g. after a submit/cancel/rollback publishes a new version). */
  refetch: () => Promise<void>;
}

/**
 * Fetches a string group's source-string snapshot from ListStrings — which resolves against
 * TRANSCOM, the source of truth for source content across all versions. describeStringGroup is
 * metadata-only now, so the strings table is loaded here instead.
 *
 * The paginated walk (and its page-count / repeated-token / no-data guards) is shared with
 * DiffModal and RollbackModal via {@link fetchVersionSnapshot}; this hook adds the table-specific
 * post-processing on top: union a stringId's variants across page boundaries, then sort by
 * stringId. When `stringGroupVersion` is omitted the latest version is served.
 *
 * Pass `skip` to defer fetching — used while the detail page is showing prefilled create-modal
 * metadata (no server-side group exists yet).
 */
export function useStringsData(
  namespaceId: string,
  stringGroupId: string,
  options?: { skip?: boolean; stringGroupVersion?: number },
): UseStringsDataResult {
  const skip = options?.skip ?? false;
  const stringGroupVersion = options?.stringGroupVersion;

  const [strings, setStrings] = useState<StringInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ thrown: unknown } | null>(null);
  const [fetchStrings] = useListStringsLazyQuery();

  // Monotonic generation stamp shared by the effect-driven load and the
  // exposed `refetch`. Every invocation claims a new generation; only the
  // latest one is allowed to write state, so a slower earlier walk that
  // resolves after a newer one (e.g. a version change, or refetch overlapping
  // the initial load) can't clobber the table with stale strings/errors.
  const loadGeneration = useRef(0);

  // Flipped true after the first successful load. Used so an error only blanks
  // the table on the initial load; a later refetch failure keeps the last good
  // snapshot on screen (see the catch below).
  const hasLoadedRef = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);

    try {
      // Shared paginated walk (guards included); this hook only owns the
      // table-specific variant union + sort below.
      const flat = await fetchVersionSnapshot(
        fetchStrings,
        namespaceId,
        stringGroupId,
        stringGroupVersion,
      );

      // A newer load superseded this one while it was walking pages — discard
      // these results rather than overwrite the fresher snapshot.
      if (generation !== loadGeneration.current) return;

      const byStringId = new Map<string, StringInfo>();
      for (const stringInfo of flat) {
        mergeString(byStringId, stringInfo);
      }
      setStrings(
        [...byStringId.values()].sort((left, right) =>
          left.stringId.localeCompare(right.stringId),
        ),
      );
      hasLoadedRef.current = true;
    } catch (loadError) {
      if (generation !== loadGeneration.current) return;
      setError({ thrown: loadError });
      // Preserve the last good snapshot on a refetch failure (e.g. a transient
      // not-ready right after a submit) so the table doesn't flash empty; only
      // the initial load, which has nothing to preserve, clears to [].
      if (!hasLoadedRef.current) {
        setStrings([]);
      }
    } finally {
      // Only the latest generation owns the loading flag; a superseded walk
      // must not flip it off while the newer load is still in flight.
      if (generation === loadGeneration.current) {
        setLoading(false);
      }
    }
    // No `t` here by design: this callback is an effect dependency below, so depending on it
    // would re-fetch the whole strings table on every locale change.
  }, [namespaceId, stringGroupId, stringGroupVersion, fetchStrings]);

  useEffect(() => {
    if (skip) return;
    void load();
  }, [skip, load]);

  return { strings, loading, error, refetch: load };
}

/**
 * Merges an incoming string into the accumulator, unioning variants by cxId when the same
 * stringId spans page boundaries.
 */
function mergeString(target: Map<string, StringInfo>, incoming: StringInfo) {
  const existing = target.get(incoming.stringId);
  if (!existing) {
    target.set(incoming.stringId, incoming);
    return;
  }
  const variants = new Map(
    existing.variants.map((variant) => [variant.cxId, variant]),
  );
  incoming.variants.forEach((variant) => variants.set(variant.cxId, variant));
  target.set(incoming.stringId, {
    ...existing,
    ...incoming,
    variants: [...variants.values()],
  });
}
